import { useState } from "react";
import { Mail, Phone, MapPin, CheckCircle2 } from "lucide-react";
import { useOutletContext } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { PageHero } from "./About";

const TYPES = ["Property enquiry", "Agency partnership", "Listing enquiry", "Referral partnership", "Technical support", "General enquiry", "Complaint", "Data protection request"];

export default function Contact() {
  const { settings } = useOutletContext();
  const [form, setForm] = useState({ full_name: "", email: "", phone: "", enquiry_type: "General enquiry", subject: "", message: "", consent: false, honeypot: "" });
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent) { toast.error("Please provide consent."); return; }
    setBusy(true);
    try { await api.post("/contact", form); setDone(true); toast.success("Message sent."); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Could not send."); }
    finally { setBusy(false); }
  };

  return (
    <div>
      <PageHero kicker="Get in touch" title="Contact Homio" subtitle="Have a question? Send us a message and we'll respond as soon as we can." />
      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-14 grid lg:grid-cols-[1fr_1.3fr] gap-12">
        <div className="space-y-4">
          {settings?.contact_phone && <ContactRow icon={Phone} label="Phone" value={settings.contact_phone} />}
          {settings?.contact_email && <ContactRow icon={Mail} label="Email" value={settings.contact_email} />}
          {settings?.business_address && <ContactRow icon={MapPin} label="Address" value={settings.business_address} />}
          <div className="rounded-2xl bg-muted p-5 text-sm text-muted-foreground">
            Homio is a referral platform. For queries about a specific property, please use the enquiry form on the property page so we can refer you to the right partner.
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-border shadow-sm p-8">
          {done ? (
            <div className="text-center py-10" data-testid="contact-success">
              <CheckCircle2 className="w-14 h-14 text-green-600 mx-auto" />
              <h3 className="mt-4 font-display text-2xl font-bold">Message sent</h3>
              <p className="mt-2 text-muted-foreground">Thank you for contacting us. We'll be in touch shortly.</p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4" data-testid="contact-form">
              <input type="text" tabIndex={-1} className="hidden" value={form.honeypot} onChange={(e) => set("honeypot", e.target.value)} aria-hidden />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5"><Label>Full name *</Label><Input required value={form.full_name} onChange={(e) => set("full_name", e.target.value)} data-testid="contact-name" /></div>
                <div className="space-y-1.5"><Label>Email *</Label><Input type="email" required value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="contact-email" /></div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5"><Label>Phone</Label><Input value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="contact-phone" /></div>
                <div className="space-y-1.5"><Label>Enquiry type</Label>
                  <Select value={form.enquiry_type} onValueChange={(v) => set("enquiry_type", v)}>
                    <SelectTrigger data-testid="contact-type"><SelectValue /></SelectTrigger>
                    <SelectContent>{TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-1.5"><Label>Subject</Label><Input value={form.subject} onChange={(e) => set("subject", e.target.value)} data-testid="contact-subject" /></div>
              <div className="space-y-1.5"><Label>Message *</Label><Textarea required rows={5} value={form.message} onChange={(e) => set("message", e.target.value)} data-testid="contact-message" /></div>
              <label className="flex items-start gap-3 text-sm text-muted-foreground cursor-pointer">
                <Checkbox checked={form.consent} onCheckedChange={(v) => set("consent", !!v)} className="mt-0.5" data-testid="contact-consent" />
                <span>I consent to Homio using my details to respond to my enquiry. *</span>
              </label>
              <Button type="submit" disabled={busy} className="w-full h-11 rounded-full bg-[#C81D35] hover:bg-[#a5162b]" data-testid="contact-submit">{busy ? "Sending..." : "Send message"}</Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

function ContactRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-4 bg-white rounded-2xl border border-border p-5">
      <div className="w-10 h-10 rounded-full bg-[#C81D35]/10 flex items-center justify-center shrink-0"><Icon className="w-5 h-5 text-[#C81D35]" /></div>
      <div><p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">{label}</p><p className="font-semibold text-[#111827]">{value}</p></div>
    </div>
  );
}
